import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useHealthStore, type HealthStatus } from '@/stores/healthStore'
import { useUIStore } from '@/stores/uiStore'

import { API_BASE as BASE_URL } from '@/lib/apiUrl'

const ONLINE_INTERVAL_MS = 15_000
const OFFLINE_INTERVAL_MS = 5_000
const PING_TIMEOUT_MS = 8_000

/**
 * Ping /api/health. Returns 'reachable' when ANY HTTP response arrives (a 5xx
 * still proves the network works — that's a server problem, shown in-page),
 * 'unreachable' when no response arrived at all.
 */
async function pingHealth(signal: AbortSignal): Promise<'reachable' | 'unreachable'> {
  try {
    await fetch(`${BASE_URL}/api/health`, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      signal,
    })
    return 'reachable'
  } catch {
    return 'unreachable'
  }
}

/**
 * Read-only network status. Safe to call from any component — it never polls.
 * (Previously every caller started its own poller, so the shell ran two and
 * fired two "Back online" toasts per recovery — UX-016.)
 */
export function useBackendHealth(): {
  status: HealthStatus
  lastSuccess: number | null
} {
  const status = useHealthStore((s) => s.status)
  const lastSuccess = useHealthStore((s) => s.lastSuccess)
  return { status, lastSuccess }
}

/**
 * THE network poller. Mount exactly once (App.tsx <BackendHealthPoller/>).
 *
 * - Listens to the browser's online/offline events (offline shows instantly).
 * - Polls /api/health: 15s while online, 5s while offline; paused while the
 *   tab is hidden, re-pinged on return and on the `online` event.
 * - On offline → online: one "Back online" toast and a refetch of stale data.
 */
export function useBackendHealthPoller(): void {
  const status = useHealthStore((s) => s.status)
  const queryClient = useQueryClient()
  const addToast = useUIStore((s) => s.addToast)
  const prevStatusRef = useRef<HealthStatus>(status)

  useEffect(() => {
    const store = useHealthStore.getState
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let controller: AbortController | null = null

    async function tick() {
      if (cancelled) return
      if (timer) clearTimeout(timer)
      controller?.abort()
      controller = new AbortController()
      const own = controller
      const timeoutId = setTimeout(() => own.abort(), PING_TIMEOUT_MS)
      const result = await pingHealth(own.signal)
      clearTimeout(timeoutId)
      if (cancelled || own !== controller) return
      if (result === 'reachable') store().recordSuccess()
      else store().recordFailure()
      const next = store().status
      const delay = next === 'offline' ? OFFLINE_INTERVAL_MS : ONLINE_INTERVAL_MS
      timer = setTimeout(tick, delay)
    }

    function handleVisibility() {
      if (document.visibilityState === 'visible') {
        void tick()
      } else {
        if (timer) clearTimeout(timer)
        timer = null
        controller?.abort()
      }
    }
    function handleOffline() {
      store().setBrowserOnline(false)
    }
    function handleOnline() {
      store().setBrowserOnline(true)
      void tick()
    }

    if (typeof navigator !== 'undefined' && navigator.onLine === false) store().setBrowserOnline(false)
    void tick()
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
      controller?.abort()
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
    }
  }, [])

  // Fire side-effects on offline → online transition (exactly once per recovery)
  useEffect(() => {
    const prev = prevStatusRef.current
    prevStatusRef.current = status
    if (prev === 'offline' && status === 'online') {
      addToast({
        type: 'success',
        title: 'Back online',
        message: 'Refreshing your data.',
        duration: 4000,
      })
      void queryClient.invalidateQueries()
    }
  }, [status, addToast, queryClient])
}
